import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import WalletModal from '../WalletModal';
import WalletAlert from '../WalletAlert';
import style from '../../../style/components/profile/emails/RemoveEmailConfirmModal.module.scss';

const MAX_RADIO_OPTIONS = 10;

const RemoveEmailConfirmModal = ({
  isOpen,
  email,
  emails,
  errorCode,
  isOnlyWorkEmail,
  isRemoving,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedDefaultId, setSelectedDefaultId] = useState(null);

  // Reset radio selection when the modal opens/closes or the target email changes
  useEffect(() => {
    setSelectedDefaultId(null);
  }, [isOpen, email?.email_id]);

  if (!email) return null;

  // data-removing applied to all renders so the attribute is removed when
  // transitioning from confirm (isRemoving=true) to an error state (isRemoving=false)
  const removingAttr = isRemoving ? 'true' : 'false';

  // Error state: last email overall
  if (errorCode === 'LAST_EMAIL') {
    return (
      <WalletModal
        isOpen={isOpen}
        onClose={onClose}
        data-removing={removingAttr}
        showCloseButton={false}
        data-icon-variant="warning"
        secondaryButton={{
          text: t('common.button_close'),
          onClick: onClose,
          disabled: false,
        }}
      >
        <idme-icon slot="icon" name="alert-triangle" size="small" />
        <span slot="title">{t('walletEmails.deleteErrorLastTitle')}</span>
        <span slot="description">{t('walletEmails.deleteErrorLastBody')}</span>
      </WalletModal>
    );
  }

  // Last verified email — block deletion regardless of type or default status
  if (errorCode === 'LAST_VERIFIED') {
    return (
      <WalletModal
        isOpen={isOpen}
        onClose={onClose}
        data-removing={removingAttr}
        showCloseButton={false}
        data-icon-variant="warning"
        secondaryButton={{
          text: t('common.button_close'),
          onClick: onClose,
          disabled: false,
        }}
      >
        <idme-icon slot="icon" name="alert-triangle" size="small" />
        <span slot="title">
          {t('walletEmails.deleteErrorLastVerifiedTitle')}
        </span>
        <span slot="description">
          {t('walletEmails.deleteErrorLastVerifiedBody', {
            email: email.address,
          })}
        </span>
      </WalletModal>
    );
  }

  // Default email: show radio selection to pick a new default before deleting.
  // LAST_VERIFIED runs first, so verified alternatives are guaranteed here.
  // If a concurrent refetch empties the list, fall through to the generic error.
  if (errorCode === 'DEFAULT_EMAIL') {
    const verifiedAlternatives = (emails || [])
      .filter(
        (e) =>
          e.email_id !== email.email_id &&
          (e.verification_status === 'verified' ||
            e.verification_status === 'verified_pending_review'),
      )
      .slice(0, MAX_RADIO_OPTIONS);

    if (verifiedAlternatives.length > 0) {
      // Pre-select the first option if nothing is selected yet
      const effectiveSelection =
        selectedDefaultId || verifiedAlternatives[0].email_id;

      return (
        <WalletModal
          isOpen={isOpen}
          onClose={isRemoving ? undefined : onClose}
          closeOnBackdropClick={!isRemoving}
          data-removing={removingAttr}
          showCloseButton={false}
          data-icon-variant="warning"
          secondaryButton={{
            text: t('walletEmails.deleteCancelButton'),
            onClick: onClose,
            disabled: isRemoving,
          }}
          primaryButton={{
            text: isRemoving
              ? t('walletEmails.deletingButton')
              : t('walletEmails.deleteConfirmButton'),
            onClick: () =>
              onConfirm(email.email_id, {
                newDefaultId: effectiveSelection,
              }),
            disabled: isRemoving,
          }}
        >
          <idme-icon slot="icon" name="alert-triangle" size="small" />
          <span slot="title">{t('walletEmails.deleteDefaultSelectTitle')}</span>
          <span slot="description">
            <Trans
              i18nKey="walletEmails.deleteDefaultSelectBody"
              values={{ email: email.address }}
              components={{ bold: <strong /> }}
            />
          </span>
          <div slot="body">
            <fieldset className={style.radioGroup}>
              {verifiedAlternatives.map((alt) => (
                <label key={alt.email_id} className={style.radioLabel}>
                  <input
                    type="radio"
                    name="newDefaultEmail"
                    value={alt.email_id}
                    checked={effectiveSelection === alt.email_id}
                    onChange={() => setSelectedDefaultId(alt.email_id)}
                    disabled={isRemoving}
                    className={style.radioInput}
                  />
                  <span className={style.radioText}>{alt.address}</span>
                </label>
              ))}
            </fieldset>
          </div>
        </WalletModal>
      );
    }
  }

  // Error state: last personal email
  if (errorCode === 'LAST_PERSONAL') {
    return (
      <WalletModal
        isOpen={isOpen}
        onClose={onClose}
        data-removing={removingAttr}
        showCloseButton={false}
        data-icon-variant="warning"
        secondaryButton={{
          text: t('common.button_close'),
          onClick: onClose,
          disabled: false,
        }}
      >
        <idme-icon slot="icon" name="alert-triangle" size="small" />
        <span slot="title">
          {t('walletEmails.deleteErrorLastPersonalTitle')}
        </span>
        <span slot="description">
          {t('walletEmails.deleteErrorLastPersonalBody', {
            email: email.address,
          })}
        </span>
      </WalletModal>
    );
  }

  // Error state: generic (500, network failure, unknown error codes)
  if (errorCode) {
    return (
      <WalletModal
        isOpen={isOpen}
        onClose={onClose}
        data-removing={removingAttr}
        showCloseButton={false}
        data-icon-variant="warning"
        secondaryButton={{
          text: t('common.button_close'),
          onClick: onClose,
          disabled: false,
        }}
      >
        <idme-icon slot="icon" name="alert-triangle" size="small" />
        <span slot="title">{t('walletEmails.deleteErrorGenericTitle')}</span>
        <span slot="description">
          {t('walletEmails.deleteErrorGenericBody')}
        </span>
      </WalletModal>
    );
  }

  // Confirm state: work-only variant has different title/body + warning
  const title = isOnlyWorkEmail
    ? t('walletEmails.deleteWorkConfirmTitle')
    : t('walletEmails.deleteConfirmTitle');

  const description = isOnlyWorkEmail ? (
    <Trans
      i18nKey="walletEmails.deleteWorkConfirmBody"
      values={{ email: email.address }}
      components={{ bold: <strong /> }}
    />
  ) : (
    t('walletEmails.deleteConfirmBody')
  );

  return (
    <WalletModal
      isOpen={isOpen}
      onClose={isRemoving ? undefined : onClose}
      closeOnBackdropClick={!isRemoving}
      data-removing={removingAttr}
      showCloseButton={false}
      data-icon-variant="warning"
      secondaryButton={{
        text: t('walletEmails.deleteCancelButton'),
        onClick: onClose,
        disabled: isRemoving,
      }}
      primaryButton={{
        text: isRemoving
          ? t('walletEmails.deletingButton')
          : t('walletEmails.deleteConfirmButton'),
        onClick: () => onConfirm(email.email_id),
        disabled: isRemoving,
      }}
    >
      <idme-icon slot="icon" name="alert-triangle" size="small" />
      <span slot="title">{title}</span>
      <span slot="description">{description}</span>
      {isOnlyWorkEmail && (
        <div slot="body">
          <WalletAlert variant="warning">
            <Trans
              i18nKey="walletEmails.deleteWarningWork"
              values={{ email: email.address }}
              components={{ bold: <strong /> }}
            />
          </WalletAlert>
        </div>
      )}
    </WalletModal>
  );
};

RemoveEmailConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  email: PropTypes.shape({
    email_id: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
  }),
  emails: PropTypes.arrayOf(
    PropTypes.shape({
      email_id: PropTypes.string.isRequired,
      address: PropTypes.string.isRequired,
      verification_status: PropTypes.string,
    }),
  ),
  errorCode: PropTypes.string,
  isOnlyWorkEmail: PropTypes.bool,
  isRemoving: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(RemoveEmailConfirmModal);
